import React, { useCallback, useEffect, useState } from 'react'
import {
  Box,
  Button,
  TextField,
  Theme,
  Typography,
  useTheme,
} from '@mui/material'
import { useFormik } from 'formik'
import * as Yup from 'yup'
import useCustomStyles from '../../../../utils/hooks/CustomStylesHook'
import { useAppSelector } from '../../../../app/hooks'
import { useParams } from 'react-router-dom'
import { useDispatch } from 'react-redux'
import { AppDispatch } from '../../../../app/store'
import { toast } from 'react-toastify'
import {
  GET_OPD_OBJECTIVE_ENDPOINT,
  POST_OPD_OBJECTIVE_ENDPOINT,
} from '../../../../utils/Endpoints'
import {
  opdFormDataSaveApi,
  opdFormDataFetchApi,
} from '../../../../services/OpdFormsSTTService'
import { toastMessages, OBJECTIVE_PHYSIO_TEXT_FIELDS } from '../constants'
import {
  selectObjectivePhysio,
  isInitialLoadForOPDForms,
  setLoading,
  updateObjectivePhysio,
  isObjectiveOpdFormSubmitted,
  setIsObjectiveOpdFormSubmit,
} from '../opdFormsSlice'

const styles = (theme: Theme) => ({
  textFieldStyle: {
    '& .MuiInputBase-input': {
      fontSize: '12px',
      [theme.breakpoints.between('sm', 'md')]: {
        minWidth: '175px',
      },
      [theme.breakpoints.up('md')]: {
        minWidth: '250px',
      },
    },
  },
  fieldLabel: {
    textAlign: 'right',
    textWrap: 'wrap',
    fontSize: '12px',
    [theme.breakpoints.between('sm', 'md')]: {
      minWidth: '192px',
    },
    [theme.breakpoints.up('md')]: {
      minWidth: '250px',
    },
  },
  textFieldWhiteBg: {
    backgroundColor: '#ffffff',
  },
  customFontBox: {
    '& .MuiTypography-root': {
      fontFamily: 'Noto Sans, sans-serif',
      color: '#363463',
    },
    '& .MuiButton-root': {
      fontFamily: 'Open Sans, sans-serif',
      fontSize: '12px',
      border: '1px solid #ddd',
      borderRadius: '4px',
      textTransform: 'capitalize',
      width: '147px',
      margin: '2px',
      justifyContent: 'center',
      color: '#6c757d',
    },
    '& .MuiButton-contained': {
      backgroundColor: '#4682b4 !important',
      color: '#f1f3f5 !important',
      fontWeight: 600,
      border: '1px solid #4682b4 !important',
    },
  },
  submitButtonContainer: {
    [theme.breakpoints.up('md')]: {
      width: '75%',
    },
    [theme.breakpoints.between('sm', 'md')]: {
      width: '85%',
    },
  },
})

const ObjectivePhysio: React.FC = () => {
  const theme = useTheme()
  const classes = useCustomStyles(styles, theme)
  const { id } = useParams()
  const objectivePhysio: any = useAppSelector(selectObjectivePhysio)
  const dispatch = useDispatch<AppDispatch>()
  const visitType = 'physio'
  const [fhirRecordId, setFhirRecordId] = useState<string | null>(null)
  const isInitialLoadForForm = useAppSelector(isInitialLoadForOPDForms)
  const isFormSubmitted = useAppSelector(isObjectiveOpdFormSubmitted)

  const initialValues = {
    posture: objectivePhysio?.posture || '',
    gait: objectivePhysio.gait || '',
  }

  const validationSchema = Yup?.object({
    posture: Yup?.string(),
    gait: Yup?.string(),
  })

  const formik = useFormik({
    initialValues,
    validationSchema,
    onSubmit: (values, { setSubmitting }) => {
      handleObjectivePhysioSubmitForm(values, setSubmitting)
    },
    enableReinitialize: true,
  })

  const handleObjectivePhysioSubmitForm = async (
    data: Record<string, any>,
    setSubmitting: any
  ) => {
    setSubmitting(true)

    const payload = {
      encounterId: id,
      visitType: `opd/${visitType}`,
      fhirRecordId: fhirRecordId,
      ...data,
    }

    const response = await opdFormDataSaveApi(
      POST_OPD_OBJECTIVE_ENDPOINT,
      payload
    )

    if (response?.success) {
      dispatch(setIsObjectiveOpdFormSubmit(true))
      toast.success(response?.message, { autoClose: 800 })
    } else {
      toast.error(response?.message || toastMessages.Form_Submission_Error, {
        autoClose: 800,
      })
    }
    setSubmitting(false)
  }

  const getObjective = useCallback(async () => {
    if (!isInitialLoadForForm && !isFormSubmitted) return
    const getObjectiveUrl = `${GET_OPD_OBJECTIVE_ENDPOINT}${id}&visitType=opd/${visitType}`
    const response = await opdFormDataFetchApi(getObjectiveUrl)

    if (response?.success) {
      const result = response?.data
      const fhirRecordId = response?.data?.fhirRecordId
      setFhirRecordId(fhirRecordId)

      dispatch(
        updateObjectivePhysio({
          gait: result?.gait,
          posture: result?.posture,
        })
      )
    } else {
      toast.error(toastMessages.Fetch_Failed)
    }
    dispatch(setLoading(false))
  }, [dispatch, id, isInitialLoadForForm, isFormSubmitted])

  useEffect(() => {
    getObjective()
  }, [getObjective])

  return (
    <Box
      display={'flex'}
      flexDirection={'column'}
      justifyContent={'start'}
      alignItems={'center'}
      gap={1}
      width={'100%'}
      className={classes?.customFontBox}
    >
      {OBJECTIVE_PHYSIO_TEXT_FIELDS?.map(({ name, label }) => (
        <Box
          display={'flex'}
          justifyContent={'start'}
          alignItems={'center'}
          width={'fit-content'}
          key={name}
          flexWrap={'wrap'}
          gap={1}
        >
          <Typography className={classes?.fieldLabel}>{label}:</Typography>

          <TextField
            multiline
            rows={3}
            variant='outlined'
            name={name}
            inputProps={{ 'data-testid': name }}
            value={formik?.values[name as keyof typeof formik.values] || ''}
            onChange={formik?.handleChange}
            onBlur={formik?.handleBlur}
            error={
              formik?.touched[name as keyof typeof formik.values] &&
              Boolean(formik?.errors[name as keyof typeof formik.errors])
            }
            className={`${classes?.textFieldStyle} ${classes?.textFieldWhiteBg}`}
          />
        </Box>
      ))}

      <Box
        display={'flex'}
        justifyContent={'end'}
        className={classes?.submitButtonContainer}
      >
        <Button
          variant='contained'
          color='primary'
          onClick={formik?.submitForm}
          className={classes?.submitBtn}
        >
          Submit
        </Button>
      </Box>
    </Box>
  )
}

export default ObjectivePhysio
